const API_PATH = '/api/intelligence';
const ADMIN_SESSION_KEY = 'panometra_admin_session';
const ADMIN_CREDENTIALS = [
  {
    email: 'admin@panometra.vc',
    password: 'Panometra2026!',
  },
  {
    email: 'hello@panometra.com',
    password: 'Password@123',
  },
];

const normalizeItem = (item) => ({
  ...item,
  databaseId: item.databaseId || item.id,
  id: item.slug || item.id,
  slug: item.slug || '',
  title: item.title,
  subtitle: item.subtitle || '',
  publishedAt: item.published_at || item.publishedAt || '',
  date: item.published_at
    ? new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()
    : item.date,
  author: item.author || 'Panometra Research',
  category: item.category || 'PERSPECTIVE',
  imageUrl: item.image_url || item.imageUrl || '',
  image: item.image_url || item.image || 'https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&q=80',
  readTime: item.read_time || item.readTime || '5 min read',
  theme: item.theme || 'bg-zinc-900',
  content: Array.isArray(item.content) ? item.content : [],
  mediaType: item.media_type || item.mediaType || 'article',
  mediaUrl: item.media_url || item.mediaUrl || '',
  sourceUrl: item.source_url || item.sourceUrl || '',
  isPublished: item.is_published ?? item.isPublished ?? true,
});

const createSlug = (title) => (
  title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

const parseContent = (body) => (
  body
    .split(/\n{2,}/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => {
      if (text.startsWith('### ')) return { type: 'h3', text: text.replace(/^### /, '') };
      if (text.startsWith('## ')) return { type: 'h3', text: text.replace(/^## /, '') };
      if (text.startsWith('> ')) return { type: 'quote', text: text.replace(/^> /, '') };
      if (text.startsWith('![Image](') && text.endsWith(')')) {
        return { type: 'image', url: text.replace(/^!\[Image\]\(/, '').replace(/\)$/, '') };
      }
      if (/^<(ul|ol|hr|pre|blockquote)\b/i.test(text)) return { type: 'html', html: text };
      return { type: 'p', text };
    })
);

const serializeContent = (content = []) => (
  content
    .map((block) => {
      if (block.type === 'h3') return `## ${block.text}`;
      if (block.type === 'quote') return `> ${block.text}`;
      if (block.type === 'image') return `![Image](${block.url})`;
      if (block.type === 'html') return block.html;
      return block.text;
    })
    .join('\n\n')
);

const buildPayload = (formData) => ({
  title: formData.title.trim(),
  slug: createSlug(formData.slug || formData.title),
  subtitle: formData.subtitle.trim() || null,
  author: formData.author.trim() || 'Panometra Research',
  category: formData.category,
  media_type: formData.mediaType,
  image_url: formData.imageUrl.trim() || null,
  media_url: formData.mediaUrl.trim() || null,
  source_url: formData.sourceUrl.trim() || null,
  read_time: formData.readTime.trim() || null,
  published_at: formData.publishedAt || new Date().toISOString(),
  is_published: formData.isPublished,
  content: parseContent(formData.body),
});

const getFriendlyError = (error) => {
  const message = error?.message || String(error || 'Unknown error');

  if (message.includes('Failed to fetch')) {
    return 'Could not reach the Neon content database. Check the API server and DATABASE_URL.';
  }

  return message;
};

const apiRequest = async (path, options = {}) => {
  try {
    const response = await fetch(path, {
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    });
    const payload = await response.json();

    if (!response.ok || payload.success === false) {
      return {
        success: false,
        error: payload.error || 'Content database request failed.',
        data: payload.data || [],
      };
    }

    return payload;
  } catch (error) {
    return { success: false, error: getFriendlyError(error), data: [] };
  }
};

export const itemToFormData = (item) => ({
  databaseId: item.databaseId || item.id,
  title: item.title || '',
  slug: item.slug || item.id || '',
  subtitle: item.subtitle || '',
  author: item.author || 'Panometra Research',
  category: item.category || 'PERSPECTIVE',
  mediaType: item.mediaType || 'article',
  imageUrl: item.imageUrl || item.image_url || '',
  mediaUrl: item.mediaUrl || '',
  sourceUrl: item.sourceUrl || '',
  readTime: item.readTime || '5 min read',
  publishedAt: item.published_at ? item.published_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
  isPublished: item.isPublished ?? true,
  body: serializeContent(item.content),
});

export const fetchPublishedIntelligence = async () => {
  const result = await apiRequest(API_PATH);
  if (!result.success) return result;

  return {
    success: true,
    data: (result.data || []).map(normalizeItem),
  };
};

export const fetchAdminIntelligence = async () => {
  const result = await apiRequest(`${API_PATH}?admin=true`);
  if (!result.success) return result;

  return {
    success: true,
    data: (result.data || []).map(normalizeItem),
  };
};

export const createIntelligenceItem = async (formData) => {
  const payload = buildPayload(formData);
  const result = await apiRequest(API_PATH, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!result.success) return result;
  return { success: true, data: normalizeItem(result.data) };
};

export const updateIntelligenceItem = async (databaseId, formData) => {
  const result = await apiRequest(`${API_PATH}?id=${encodeURIComponent(databaseId)}`, {
    method: 'PUT',
    body: JSON.stringify(buildPayload(formData)),
  });

  if (!result.success) return result;
  return { success: true, data: normalizeItem(result.data) };
};

export const deleteIntelligenceItem = async (databaseId) => {
  return apiRequest(`${API_PATH}?id=${encodeURIComponent(databaseId)}`, {
    method: 'DELETE',
  });
};

export const signInAdmin = async ({ email, password }) => {
  const normalizedEmail = email.trim().toLowerCase();
  const admin = ADMIN_CREDENTIALS.find((credential) => (
    credential.email === normalizedEmail && credential.password === password
  ));

  if (!admin) {
    return { success: false, error: 'Invalid admin email or password.' };
  }

  const session = {
    user: { email: admin.email },
    createdAt: new Date().toISOString(),
  };

  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
  return { success: true, data: { session } };
};

export const signOutAdmin = async () => {
  localStorage.removeItem(ADMIN_SESSION_KEY);
  return { success: true };
};

export const getAdminSession = async () => {
  const rawSession = localStorage.getItem(ADMIN_SESSION_KEY);
  if (!rawSession) return { success: true, session: null };

  try {
    return { success: true, session: JSON.parse(rawSession) };
  } catch {
    localStorage.removeItem(ADMIN_SESSION_KEY);
    return { success: true, session: null };
  }
};
